import React, { memo, useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Box, List, Typography, useMediaQuery } from "@mui/material";
import { hasAuthority } from "../../../../../../utils/authorities";
import { activeItem, openDrawer } from "../../../../../../store/reducers/menu";
import { useTheme } from "@emotion/react";
import { useLocation } from "react-router";
import { useState } from "react";
import menuConfig from "../../../../../config/menu.config";
import NavItem from "./NavItem";

const NavGroup = ({ item }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const location = useLocation();
  const matchDownLG = useMediaQuery(theme.breakpoints.down('lg'));
  // Redux state
  const { drawerOpen, openItem } = useSelector(state => state.menu);
  const { auth: userAuthority = [], role_id, token, bmr_modules, bmr_submodules } = useSelector(state => state.loginData);
  // Local state
  const [activeItemId, setActiveItemId] = useState(openItem[0]);
  const [subChildMenu, setSubChildMenu] = useState([]);

  
  const firstSegment = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    if (segments[0] === 'bmr') {
      return segments[2] || segments[1];
    }
    return segments[0];
  }, [location.pathname]);
  

  // Handlers
  const handleSetActiveItemId = useCallback((id) => {
    setActiveItemId(id);
  }, []);

  const findChildInSubChildren = useCallback((children) => {
    for (const item of children) {
      if (item?.subChildren) {
        const subItem = item.subChildren?.find(sub => sub.id === firstSegment);
        if (subItem) {
          return {
            ...subItem,
            parent: item.title,
            parentId: item.id
          };
        }
      }
    }
    return null;
  }, [firstSegment]);

  const handleHighlightTab = useCallback(() => {
    const isChild = findChildInSubChildren(menuConfig.children);

    if (isChild) {
      const { parentId } = isChild;
      const newOpenItem = [parentId, firstSegment];
      dispatch(activeItem({ openItem: newOpenItem }));
      setActiveItemId(newOpenItem);
      setSubChildMenu(
        menuConfig.children?.find(op => parentId === op.id)?.subChildren || []
      );
    } else {
      const modifySegment =  firstSegment == 'qa' ? ['quality-approval']: firstSegment == 'qc' ? ['quality-control'] : [firstSegment]
      dispatch(activeItem({ openItem: modifySegment }));
      setSubChildMenu([]);
      setActiveItemId(modifySegment);
    }
  }, [dispatch, firstSegment, findChildInSubChildren]);

  // Effects
  useEffect(() => {
    if (firstSegment) {
      handleHighlightTab();
    }
  }, [firstSegment, handleHighlightTab]);

  useEffect(() => {
    if (activeItemId === "master-data" && !matchDownLG && drawerOpen) {
      dispatch(openDrawer({ drawerOpen: false }));
    }
  }, []);

  // Memoized components
  const SubChildMenuItems = useMemo(() => (
    <div className="flex flex-col gap-5 pl-[22px] pr-[22px] pt-4">
      {token && (
        <Typography variant="h6" fontWeight={500} color="white">
          {subChildMenu[0]?.parent}
        </Typography>
      )}
      {subChildMenu?.map(menuItem =>
        menuItem?.authorities &&
        (hasAuthority(menuItem.authorities, bmr_submodules) || menuItem?.authorities[0] === 'rbac') && (
          <NavItem
            key={menuItem.id}
            item={menuItem}
            level={1}
            activeItemId={activeItemId}
            setActiveItemId={handleSetActiveItemId}
          />
        )
      )}
    </div>
  ), [token, subChildMenu, bmr_submodules, activeItemId]);
  const MainMenuItems = useMemo(() => (
<div className={`border-r divider-border pt-4`}
  style={{
      flex: "0 0 auto",        // ✅ prevent shrinking
      maxHeight: "100vh",      // ✅ allow scrolling only within viewport height
      overflowY: "auto",       // ✅ scroll only parent menu
      overflowX: "hidden"
    }}>
      {item.children?.map(menuItem => {
        if (menuItem?.authorities && (hasAuthority(menuItem.authorities, bmr_modules) || menuItem?.authorities[0] === 'rbac' || menuItem?.authorities[0] === 'dashboard')) {
          const isHidden = (menuItem?.authorities[0] === 'rbac') && role_id !== 1;
          if (isHidden) return null;
          return (
            <NavItem
              key={menuItem.id}
              item={menuItem}
              level={1}
              activeItemId={activeItemId}
              setActiveItemId={handleSetActiveItemId}
              setSubChildMenu={setSubChildMenu}
            />
          );
        }
        return null;
      })}
    </div>
  ), [item.children, userAuthority, role_id, activeItemId]);

  const ListHeader = useMemo(() => (
    item.title && drawerOpen ? (
      <Box sx={{ pl: 3, mb: 1.5 }}>
        <Typography variant="subtitle2" color="textSecondary">
          {item.title}
        </Typography>
      </Box>
    ) : null
  ), [item.title, drawerOpen]);

  return (
    <List
      subheader={ListHeader}
      sx={{
        height: "100%",
        mb: drawerOpen ? 0 : 0,
        py: 0,
        zIndex: 0,
        display: "flex"
      }}
    >
      {MainMenuItems}
      {subChildMenu?.length > 0 && SubChildMenuItems}
    </List>
  );
};

NavGroup.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string,
    children: PropTypes.arrayOf(PropTypes.object)
  }).isRequired
};

export default memo(NavGroup);